import { useEffect, useState } from "react";
import { Link, useOutletContext, useParams } from "react-router-dom";
import { toast } from "sonner";
import { Badge } from "../../components/ui/badge";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Separator } from "../../components/ui/separator";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { highlevelConversationsService } from "../../services/highlevelConversationsService";
import { ArrowLeft, MessageCircle, Phone, Mail, CalendarCheck, Send, School, Clock, UserRound } from "lucide-react";

export default function LeadDetail() {
  const { vertical, config } = useOutletContext();
  const { leadId } = useParams();
  const [convo, setConvo] = useState(null);
  const [stageId, setStageId] = useState(null);

  const lead = config.leads.find((l) => l.contact_id === leadId);

  useEffect(() => {
    if (vertical === "enrichment" && lead) {
      highlevelConversationsService.getConversationForContact(lead.contact_id).then(setConvo);
    } else {
      setConvo(null);
    }
    setStageId(lead?.stage ?? null);
  }, [vertical, leadId]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!lead) {
    return (
      <div className="max-w-lg mx-auto text-center py-20" data-testid="lead-not-found">
        <p className="font-heading font-semibold text-lg text-foreground">This {config.entityLabel.toLowerCase()} isn't in the {config.label} dataset</p>
        <p className="text-sm text-muted-foreground mt-2">You may have switched vertical view. Head back to the pipeline to pick a {config.entityLabel.toLowerCase()}.</p>
        <Button className="rounded-xl mt-5" asChild data-testid="lead-not-found-back-button">
          <Link to="/demo/pipeline"><ArrowLeft className="h-4 w-4 mr-1.5" /> Back to pipeline</Link>
        </Button>
      </div>
    );
  }

  const stageName = config.stages.find((s) => s.id === stageId)?.name || lead.stage_name;

  const handleStageChange = (id) => {
    setStageId(id);
    const s = config.stages.find((x) => x.id === id);
    toast.success(`${lead.parent_name} moved to “${s.name}”`);
  };

  const quickAction = (label) => toast.success(`${label} — queued for ${lead.parent_name} (demo)`);

  const fmtDate = (iso) => iso ? new Date(iso).toLocaleString("en-SG", { weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";

  const customFields = vertical === "enrichment" ? [
    ["Child", `${lead.child_name} (${lead.child_age} yrs)`],
    ["Level", lead.child_level],
    ["School", lead.school_name],
    ["Programme interest", lead.programme_interest],
    ["Preferred timing", lead.preferred_days_times],
    ["Class vacancy", lead.class_vacancy_status],
    ["Trial approval", lead.trial_approval_status],
    ["Trial date", fmtDate(lead.trial_date)],
    ["Enrolment status", lead.enrolment_status],
    ["Waitlist", lead.waitlist_status],
    ["Re-enrolment", lead.re_enrolment_status],
    ["Sibling interest", lead.sibling_interest ? "Yes — flagged for sibling playbook" : "Not flagged"],
    ["Review / referral", lead.review_request_status],
  ] : [
    [config.programmeLabel, lead.programme_name],
    [config.branchLabel, lead.branch_name],
    ["Source", lead.source],
    ["Est. value", `$${lead.monetary_value}`],
    ["Next action", lead.next_action],
  ];

  return (
    <div className="space-y-4 animate-fade-up max-w-6xl">
      <div className="flex items-center gap-3">
        <Button variant="secondary" size="sm" className="rounded-xl border border-border" asChild data-testid="lead-detail-back-button">
          <Link to="/demo/pipeline"><ArrowLeft className="h-4 w-4 mr-1" /> Pipeline</Link>
        </Button>
      </div>

      {/* Header */}
      <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]" data-testid="lead-detail-header">
        <CardContent className="p-5 sm:p-6">
          <div className="flex flex-col md:flex-row md:items-center gap-4 justify-between">
            <div className="flex items-center gap-4">
              <span className="h-12 w-12 rounded-2xl bg-[hsl(199_78%_92%)] text-[hsl(215_84%_22%)] font-heading font-semibold flex items-center justify-center text-lg shrink-0">
                {lead.parent_name.split(" ").pop()[0]}
              </span>
              <div>
                <p className="font-heading font-semibold text-lg text-foreground">{lead.parent_name}</p>
                <p className="text-sm text-muted-foreground">
                  {lead.child_name ? `Parent of ${lead.child_name} · ` : ""}{lead.programme_name} · {lead.branch_name}
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <Badge className="bg-[hsl(188_62%_90%)] text-[hsl(188_62%_28%)] hover:bg-[hsl(188_62%_90%)]" data-testid="lead-detail-status">{stageName}</Badge>
              <Select value={stageId ?? undefined} onValueChange={handleStageChange}>
                <SelectTrigger className="h-9 rounded-xl w-[180px] text-xs bg-white" data-testid="lead-detail-stage-select">
                  <SelectValue placeholder="Move stage" />
                </SelectTrigger>
                <SelectContent className="max-h-72">
                  {config.stages.map((s) => (
                    <SelectItem key={s.id} value={s.id} className="text-xs">{s.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          <div className="flex flex-wrap gap-x-5 gap-y-1.5 mt-4 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5"><Phone className="h-3.5 w-3.5" /> {lead.phone || "+65 9xxx xxxx"}</span>
            <span className="inline-flex items-center gap-1.5"><Mail className="h-3.5 w-3.5" /> {lead.email || "demo@example.com"}</span>
            <span className="inline-flex items-center gap-1.5"><UserRound className="h-3.5 w-3.5" /> Owner: {lead.assigned_user_name}</span>
            <span className="inline-flex items-center gap-1.5"><Clock className="h-3.5 w-3.5" /> First response: {lead.first_response_mins} min</span>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Left: fields + timeline */}
        <div className="lg:col-span-2 space-y-4">
          <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <p className="font-semibold text-sm text-foreground mb-4 flex items-center gap-1.5"><School className="h-4 w-4 text-accent" /> Profile & custom fields</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3">
                {customFields.map(([label, value]) => (
                  <div key={label} className="flex items-start justify-between gap-3 border-b border-border pb-2">
                    <p className="text-xs text-muted-foreground">{label}</p>
                    <p className="text-xs font-medium text-foreground text-right">{value || "—"}</p>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <p className="font-semibold text-sm text-foreground mb-4 flex items-center gap-1.5"><MessageCircle className="h-4 w-4 text-accent" /> Conversation</p>
              {convo ? (
                <div className="space-y-3">
                  <Badge variant="secondary" className="text-[10px]">{convo.channel}</Badge>
                  {convo.messages.map((m, i) => (
                    <div key={i} className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${m.direction === "inbound" ? "bg-secondary text-foreground" : "bg-[hsl(215_84%_22%)] text-white ml-auto"}`}>
                      {m.body}
                      <p className={`text-[10px] mt-1 ${m.direction === "inbound" ? "text-muted-foreground" : "text-[hsl(210_40%_80%)]"}`}>
                        {m.direction === "outbound" ? `${m.sender} · ` : ""}{new Date(m.at).toLocaleString("en-SG", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-muted-foreground py-4 text-center">No conversation thread in this demo dataset — message history from WhatsApp, SMS, and email would appear here.</p>
              )}
              <Separator className="my-4" />
              <div className="flex gap-2">
                <Button size="sm" className="rounded-xl flex-1" onClick={() => quickAction("WhatsApp reply")} data-testid="lead-detail-whatsapp-button">
                  <Send className="h-3.5 w-3.5 mr-1.5" /> Send WhatsApp
                </Button>
                <Button size="sm" variant="secondary" className="rounded-xl flex-1 border border-border" onClick={() => quickAction("Trial slot offer")} data-testid="lead-detail-offer-slot-button">
                  <CalendarCheck className="h-3.5 w-3.5 mr-1.5" /> Offer slot
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right: next action + timeline */}
        <div className="space-y-4">
          <Card className="rounded-2xl border-2 border-[hsl(188_62%_33%)] bg-[hsl(188_62%_97%)] shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <p className="text-xs font-semibold text-[hsl(188_62%_28%)] uppercase tracking-wide mb-1.5">Next action</p>
              <p className="text-sm font-medium text-foreground leading-relaxed" data-testid="lead-detail-next-action">{lead.next_action}</p>
              <Button size="sm" className="rounded-xl w-full mt-4" onClick={() => quickAction("Next action")} data-testid="lead-detail-complete-action-button">Mark done & advance</Button>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <p className="font-semibold text-sm text-foreground mb-4">Timeline</p>
              <div className="space-y-4">
                {[
                  { label: "Enquiry received", value: fmtDate(lead.created_date), done: true },
                  { label: "Last activity", value: fmtDate(lead.last_activity_date), done: true },
                  ...(lead.trial_date ? [{ label: lead.trial_attended ? "Trial attended" : "Trial scheduled", value: fmtDate(lead.trial_date), done: lead.trial_attended }] : []),
                  { label: "Next follow-up", value: "Auto-scheduled", done: false },
                ].map((t, i) => (
                  <div key={i} className="flex gap-3">
                    <div className="flex flex-col items-center">
                      <span className={`h-2.5 w-2.5 rounded-full mt-1 ${t.done ? "bg-[hsl(188_62%_33%)]" : "bg-[hsl(214_20%_80%)]"}`} />
                      {i < 3 && <span className="w-px flex-1 bg-border mt-1" />}
                    </div>
                    <div className="pb-1">
                      <p className="text-xs font-medium text-foreground">{t.label}</p>
                      <p className="text-[11px] text-muted-foreground">{t.value}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-2xl border-border shadow-[var(--shadow-sm)]">
            <CardContent className="p-5">
              <p className="font-semibold text-sm text-foreground mb-2">Source & attribution</p>
              <div className="flex flex-wrap gap-1.5">
                <Badge variant="secondary" className="text-[10px]">{lead.source}</Badge>
                <Badge variant="secondary" className="text-[10px] bg-[hsl(199_78%_92%)] text-[hsl(215_84%_22%)]">{lead.branch_name}</Badge>
                <Badge variant="secondary" className="text-[10px]">${lead.monetary_value}/mo est.</Badge>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
